/**
 * page-application.php のフォーム用
 * * 役割：
 * 1. 入力画面におけるCF7の標準Ajax送信を完全に阻止（type="button" または e.preventDefault）
 * 2. クライアントサイド（JS）で詳細なバリデーションを実行
 * 3. バリデーション成功時、入力データを sessionStorage にJSON形式で保存
 * 4. 送信処理は行わず、指定された「確認画面（data-confirm-url）」へ即時遷移させる
 */
const SELECTOR_FORM = '[data-js-application-form]';
const MSG_REQUIRED = '必須項目です';

function showError(el, show, message) {
  if (!el) {
    return;
  }
  if (message) {
    el.textContent = message;
  }
  el.classList.toggle('is-visible', show);
  el.toggleAttribute('hidden', !show);
}

function toggleInputError(inputWrap, show) {
  if (!inputWrap) {
    return;
  }
  inputWrap.classList.toggle('has-error', show);
}

function isFilled(value) {
  return typeof value === 'string' && value.trim() !== '';
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isDigitsOnlyLen7(value) {
  return /^\d{7}$/.test(value.trim().replace(/\D/g, ''));
}

export default class ApplicationForm {
  init() {
    const form = document.querySelector(SELECTOR_FORM);
    if (!form) {
      return;
    }

    // --- 【修正】データ復元処理（確認画面から戻った時のみ実行） ---
    const savedData = sessionStorage.getItem('application_data');
    const referrer = document.referrer; // どこから来たかを取得

    // 保存データがある かつ、遷移元URLに「confirm」が含まれている場合のみ復元
    if (savedData && referrer.includes('confirm')) {
      try {
        const data = JSON.parse(savedData);
        Object.keys(data).forEach(key => {
          const input = form.querySelector(`[name="${key}"]`);
          if (input) {
            if (input.type === 'radio') {
              const radio = form.querySelector(`[name="${key}"][value="${data[key]}"]`);
              if (radio) radio.checked = true;
            } else {
              input.value = data[key];
            }
          }
        });
      } catch (e) {
        // 解析エラー時は何もしない
      }
    } else {
      // 求人ページなど「確認画面以外」から来た場合は、古いデータを破棄してリセット
      sessionStorage.removeItem('application_data');
    }

    const confirmUrl = form.getAttribute('data-confirm-url');
    const customBtn = form.querySelector('#custom-confirm-btn');
    if (!customBtn) {
      console.error('カスタムボタンが見つかりません: #custom-confirm-btn');
      return;
    }

    const fields = [
      {
        input: form.querySelector('#application-name'),
        error: form.querySelector('#application-name-error'),
        required: true,
      },
      {
        input: form.querySelector('#application-furigana'),
        error: form.querySelector('#application-furigana-error'),
        required: true,
      },
      {
        input: form.querySelector('#application-age'),
        error: form.querySelector('#application-age-error'),
        required: true,
        type: 'age',
      },
      {
        input: form.querySelector('#application-postal'),
        error: form.querySelector('#application-postal-error'),
        required: true,
        type: 'postal',
      },
      {
        input: form.querySelector('#application-address'),
        error: form.querySelector('#application-address-error'),
        required: true,
      },
      {
        input: form.querySelector('#application-phone'),
        error: form.querySelector('#application-phone-error'),
        required: true,
      },
      {
        input: form.querySelector('#application-email'),
        error: form.querySelector('#application-email-error'),
        required: true,
        type: 'email',
      },
    ];

    // フィールドの取得漏れチェック
    fields.forEach(f => {
      if (!f.input) console.warn('入力要素が見つかりません', f);
    });

    // --- フォーム送信の完全なブロック ---
    // Enterキーや他のサブミットコントロールからの直接送信を防止
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });

    customBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      let firstInvalid = null;
      let allOk = true;

      fields.forEach((entry) => {
        if (!entry.input) {
          return;
        }
        const wrap = entry.input.closest('.p-contact-form__input');
        let ok = true;
        const v = entry.input.value;

        let errMsg = MSG_REQUIRED;

        // --- バリデーション ---
        // 1. 必須チェック
        if (entry.required && !isFilled(v)) {
          ok = false;
        }
        // 2. 年齢チェック
        if (ok && entry.type === 'age') {
          const n = parseInt(v, 10);
          if (Number.isNaN(n) || n < 0) {
            ok = false;
            errMsg = '0以上の数字を入力してください';
          }
        }
        // 3. 郵便番号チェック
        if (ok && entry.type === 'postal' && isFilled(v)) {
          if (!isDigitsOnlyLen7(v)) {
            ok = false;
            errMsg = '7桁の半角数字で入力してください';
          }
        }
        // 4. メールアドレス形式チェック
        if (ok && entry.type === 'email') {
          if (isFilled(v) && !isValidEmail(v)) {
            ok = false;
            errMsg = 'メールアドレスの形式が正しくありません';
          }
        }

        if (!ok) {
          allOk = false;
          showError(entry.error, true, errMsg);
          toggleInputError(wrap, true);
          if (!firstInvalid) {
            firstInvalid = entry.input;
          }
        } else {
          showError(entry.error, false);
          toggleInputError(wrap, false);
        }
      });

      if (allOk) {
        // 実際の form タグ（CF7の中身）からデータを取得
        const actualForm = form.querySelector('form');
        if (!actualForm) {
          console.error('実際のform要素が見つかりません');
          return;
        }

        const formData = new FormData(actualForm);
        const data = {};

        formData.forEach((value, key) => {
          // CF7内部用パラメータ以外を保存
          if (!key.startsWith('_')) data[key] = value;
        });

        // 自由記入欄(textarea)のIDが message の場合の保険
        const messageEl = actualForm.querySelector('#application-message');
        if (messageEl) data['message'] = messageEl.value;

        sessionStorage.setItem('application_data', JSON.stringify(data));

        const prepareUrl = form.dataset.prepareConfirmUrl;
        const nonce = form.dataset.prepareConfirmNonce;
        try {
          const response = await fetch(prepareUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: new URLSearchParams({
              action: 'mira_prepare_confirm',
              nonce,
              form_type: 'application',
            }),
          });
          const result = await response.json();
          if (!response.ok || !result.success) {
            throw new Error('確認画面への遷移準備に失敗しました');
          }
          window.location.href = confirmUrl;
        } catch (error) {
          console.error(error);
          window.alert('通信エラーが発生しました。時間をおいて再度お試しください。');
        }
      } else {
        if (firstInvalid) firstInvalid.focus();
      }
    });

    // 入力時にエラーを消す処理を追加
    fields.forEach((entry) => {
      if (!entry.input) return;
      ['input', 'change'].forEach(eventType => {
        entry.input.addEventListener(eventType, () => {
          const wrap = entry.input.closest('.p-contact-form__input');
          showError(entry.error, false);
          toggleInputError(wrap, false);
        });
      });
    });
  }
}